import { Localized, useLocalization } from "@fluent/react";
import {
  Button,
  ContentCard,
  Dialog,
  Row,
  Spinner,
  TextInput,
  Tooltip
} from "@narmi/design_system";
import { useState } from "react";
import cc from "classcat";
import { useRequestWithFeedback } from "byzantine/src/dbbl/composites/request/request.hooks";
import { useNotificationContext } from "cerulean";
import type User from "byzantine/src/User";
import type { TotpDevice, TotpSetupError } from "./types";
import useFetchTotpDevice from "./useFetchTotpDevice";
import styles from "./AddTotpMfaDevice.module.scss";

const AddedAppText = ({ nickname }: { nickname: string }) => {
  const trimmedNickname = nickname.trim();

  return (
    <Localized
      id="two-factor-authentication-totp-confirmation"
      elems={{ b: <b /> }}
      vars={{
        nickname: trimmedNickname,
        hasNickname: `${trimmedNickname.length > 0}`
      }}
    >
      <div />
    </Localized>
  );
};

const AddAppText = ({ onInfoClick }: { onInfoClick: () => void }) => (
  <Localized
    id="two-factor-authentication-totp-recommendation"
    elems={{
      infoLink: <a className={styles.infoLink} onClick={onInfoClick} href="#" />
    }}
  >
    <div />
  </Localized>
);

type AddTotpMfaDeviceProps = {
  user: User;
  onDeviceAdded: () => void;
};

const AddTotpMfaDevice = ({ user, onDeviceAdded }: AddTotpMfaDeviceProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [nickname, setNickname] = useState("");
  const [isAdded, setIsAdded] = useState(false);
  const { send: confirmSetup, loading: isSubmitting } = useRequestWithFeedback<
    { device: TotpDevice },
    TotpSetupError
  >();
  const endpoint = `users/${user.uuid}/mfa_devices`;
  const { deviceSetup, isLoading } = useFetchTotpDevice(endpoint);

  const canVerify =
    !!deviceSetup &&
    !isSubmitting &&
    verificationCode.length === deviceSetup.digits &&
    /^\d+$/.test(verificationCode);

  const onVerifyCode = () => {
    if (!deviceSetup || !canVerify) return;
    const trimmedNickname = nickname.trim();
    if (trimmedNickname.length > deviceSetup.max_name_length) {
      sendNotification({
        type: "negative",
        text: l10n.getString("totp-nickname-too-long", {
          maxLength: deviceSetup.max_name_length
        })
      });
      return;
    }
    confirmSetup({
      action: {
        url: endpoint,
        options: {
          method: "POST",
          payload: {
            device_type: "totp",
            uuid: deviceSetup.uuid,
            token: verificationCode,
            custom_name: trimmedNickname
          }
        }
      },
      onSuccess: () => {
        setIsAdded(true);
        onDeviceAdded();
      },
      onError: async (err) => {
        try {
          const data = await err.response?.json();
          // A device setup lasts 10 minutes so it's unlikely a user runs into this scenario
          if (
            data?.id === "totp_setup_expired" ||
            data?.id === "totp_setup_attempts_exceeded" ||
            data?.id === "not_found"
          ) {
            sendNotification({
              type: "negative",
              text: l10n.getString("totp-setup-invalid-reload")
            });
            return;
          }
          const fieldMessage = [data?.token, data?.custom_name]
            .filter(Array.isArray)
            .flat()
            .find(
              (message) => typeof message === "string" && message.length > 0
            );
          if (fieldMessage) {
            sendNotification({ type: "negative", text: fieldMessage });
            return;
          }
          if (typeof data?.message === "string") {
            const message = data.message || l10n.getString("error-generic");
            sendNotification({ type: "negative", text: message });
            return;
          }
        } catch {
          // Use the generic message when the error response cannot be read.
        }
        sendNotification({
          type: "negative",
          text: l10n.getString("error-generic")
        });
      }
    });
  };

  return (
    <div>
      <Dialog
        title={l10n.getString("title-authenticator-apps")}
        isOpen={isInfoOpen}
        onUserDismiss={() => setIsInfoOpen(false)}
      >
        <Localized
          id="description-authenticator-apps-dialog"
          elems={{ br: <br /> }}
        >
          <p />
        </Localized>
      </Dialog>
      <ContentCard kind="elevated">
        <h4 className="margin--bottom--xs">
          {l10n.getString("title-two-factor-authentication")}{" "}
          {isAdded && <span className="narmi-icon-check fontColor--success" />}
        </h4>
        <div>
          {isAdded ? (
            <AddedAppText nickname={nickname} />
          ) : (
            <AddAppText onInfoClick={() => setIsInfoOpen(true)} />
          )}
        </div>
        <div
          className={cc([styles.content, isAdded && styles.contentHidden])}
          inert={isAdded}
        >
          <div className={styles.divider} />
          <div className={styles.container}>
            {deviceSetup && (
              <Tooltip
                text={l10n.getString("totp-seed", {
                  seed: deviceSetup.manual_entry_key
                })}
              >
                <img
                  src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(
                    deviceSetup.qr_svg
                  )}`}
                  alt={l10n.getString("totp-seed", {
                    seed: deviceSetup.manual_entry_key
                  })}
                  className={styles.containerQrBox}
                />
              </Tooltip>
            )}
            {isLoading && (
              <div className={styles.containerQrBox} role="status">
                <Spinner size={60} color="var(--theme-secondary)" />
              </div>
            )}

            <div>
              <TextInput
                label={l10n.getString("totp-verification-code-label")}
                type="text"
                inputMode="numeric"
                maxLength={deviceSetup?.digits}
                disabled={!deviceSetup || isSubmitting}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
              />
              <Row alignItems="center">
                <Row.Item>
                  <TextInput
                    label={l10n.getString("totp-nickname-label")}
                    value={nickname}
                    disabled={!deviceSetup || isSubmitting}
                    onChange={(e) => setNickname(e.target.value)}
                  />
                </Row.Item>
                <Row.Item shrink>
                  <Button
                    kind="tonal"
                    type="button"
                    disabled={!canVerify}
                    onClick={onVerifyCode}
                  >
                    {l10n.getString("label-enable")}
                  </Button>
                </Row.Item>
              </Row>
            </div>
          </div>
        </div>
      </ContentCard>
    </div>
  );
};

export default AddTotpMfaDevice;
